import { useEffect } from 'react';

import chinobiAvatar from '@/assets/images/agents/chinobi.png';
import { chinobiHeroVideo, chinobiHeroVideoPoster } from '@/assets/videos';
import AgentAssistantBar from '@/components/chat/assistant-bar/agent-assistant-bar';
import PageSeo from '@/components/seo/page-seo';
import { ROUTES } from '@/constants/routes';
import { usePublicAuthStatus } from '@/hooks/use-public-auth-status';
import { cn } from '@/lib/tw-merge/cn';
import CtaSection from '@/pages/site/home-page/components/cta-section';
import LatestNewsSection from '@/pages/site/home-page/components/latest-news-section';
import { withPostLoginRedirect } from '@/utils/auth-redirect';
import { loadLinkedInInsightTag } from '@/utils/linkedin-insight';

import Hero from '../home-page/components/hero-section';
import IntegrationsLogoMarquee from '../integrations-page/components/integrations-logo-marquee';
import ChinobiFaq from './components/chinobi-faq';
import OperationSection from './components/chinobi-operations-section';
import ExperienceSection from './components/experience-section';
import IntegrationMetrics from './components/integration-metrics';
import PersonalizedAnswersSection from './components/personalize-answers-section';
import ValueDeliverySection from './components/value-delivery';

const MeetLiveAIPage = () => {
  const { isAuthenticated } = usePublicAuthStatus();

  useEffect(() => {
    loadLinkedInInsightTag();
  }, []);

  const chinobiCtaPath = isAuthenticated
    ? ROUTES.DASHBOARD_VIDEO_CHAT_CHINOBI
    : withPostLoginRedirect(
        ROUTES.CHINOBI_SIGNUP,
        ROUTES.DASHBOARD_VIDEO_CHAT_CHINOBI
      );

  return (
    <div>
      <PageSeo
        title="Meet Chinobi"
        description="Meet Chinobi for a live conversation about applying autonomous AI to specialty-finance origination, underwriting, and collections."
        path={ROUTES.MEET_LIVE_AI}
      />
      <Hero
        title={
          <>
            Tell Chinobi the <br /> challenge. Explore <br /> what comes next.
          </>
        }
        description="Video chat with Chinobi to reimagine origination, underwriting, collections, and the future of specialty finance powered by autonomous AI agents."
        titleClassName={cn(
          'font-fraunces text-[29px] font-semibold leading-[34px] tracking-[0.2px] capitalize',
          'sm:text-[36px] sm:leading-[40px]',
          'md:text-[48px] md:leading-[52px]',
          'lg:text-[56px] lg:leading-[60px]',
          // Compact laptop viewports use progressively smaller title sizes.
          '[@media(min-width:1024px)_and_(max-height:900px)]:text-[48px]',
          '[@media(min-width:1024px)_and_(max-height:900px)]:leading-[52px]',
          '[@media(min-width:1024px)_and_(max-height:850px)]:!text-[38.4px]',
          '[@media(min-width:1024px)_and_(max-height:850px)]:!leading-[41.6px]',
          // Keep this three-line title at its custom desktop scale on monitors.
          '[@media(min-width:1920px)_and_(min-height:1000px)]:!text-[56px]',
          '[@media(min-width:1920px)_and_(min-height:1000px)]:!leading-[60px]'
        )}
        bgVideo={chinobiHeroVideo}
        bgPoster={chinobiHeroVideoPoster}
        showVideoAudioControl
        playVideoWithSound
        backgroundMediaVisibleClassName="opacity-85"
        // backgroundOverlayClassName="bg-transparent"
        videoClassName="absolute inset-0 h-full w-full object-cover object-[72%_center] transition-opacity duration-500 md:object-[68%_center] lg:object-[60%_center]"
        // backgroundOverlayClassName="bg-[linear-gradient(323.73deg,rgba(11,48,107,0)_34.98%,rgba(15,33,62,0.340291)_44.65%,rgba(18,18,18,0.36)_63.64%,rgba(18,18,18,0.36)_81.9%,rgba(18,18,18,0.36)_98.65%)]"
        primaryCta={{
          label: 'Video Call',
          to: chinobiCtaPath,
        }}
        secondaryCta={{
          label: 'Schedule a Demo',
          to: ROUTES.CONTACT,
        }}
        showPartnerLogos={false}
      />
      <section className="app-container py-16 text-center">
        <p className="mb-8 text-sm font-normal uppercase text-foreground">
          Integrated with
        </p>
        <IntegrationsLogoMarquee tone="dark" gapClass="gap-12 md:gap-20" />
        <IntegrationMetrics />
      </section>
      {/* 1. Operation Section: Animated static-position card carousel */}
      <OperationSection />

      {/* 2. The Experience Section: What Happens In A Live Consultation */}
      <ExperienceSection />

      {/* 3. Personalized Answers Section: Dynamic Accordion Cards */}
      <PersonalizedAnswersSection chinobiCtaPath={chinobiCtaPath} />

      {/* 4. Value Delivery Section: How Chinobi delivers value */}
      <ValueDeliverySection />

      <ChinobiFaq />

      {/* 5. Latest News Section */}
      <LatestNewsSection variant="ledger" />

      {/* 6. CTA Section */}
      <CtaSection
        headingClassName="text-2xl leading-[28px]"
        primaryCta={{
          label: 'Speak with Chinobi',
          to: chinobiCtaPath,
          showVideoIcon: true,
          avatar: {
            src: chinobiAvatar,
            alt: 'Chinobi',
          },
        }}
      />

      <AgentAssistantBar
        agentKey="chinobi"
        agentName="Chinobi"
        avatar={chinobiAvatar}
      />
    </div>
  );
};

export default MeetLiveAIPage;
